import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  CancelPlayerDepositLimitMutation,
  CancelPlayerDepositLimitMutationVariables,
} from './__generated__/PlayerCancelDepositLimitForm';

const query = graphql`
  query SanityPlayerCancelDepositLimitForm {
    sanityPlayerCancelDepositLimitsForm {
      title {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const cancelPendingDepositLimitMutation = gql`
  mutation CancelPlayerDepositLimit(
    $playerId: ID!
    $limitPeriodType: LimitTypeEnum!
  ) {
    cancelPendingDepositLimit(
      playerId: $playerId
      limitPeriodType: $limitPeriodType
    ) {
      id
      depositLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerCancelDepositLimitForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
}> = ({ playerId, limitPeriodType }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerCancelDepositLimitFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerCancelDepositLimitsForm;

  const [playerCancelDepositLimitState, cancelPendingDepositLimit] =
    useMutation<
      CancelPlayerDepositLimitMutation,
      CancelPlayerDepositLimitMutationVariables
    >(cancelPendingDepositLimitMutation);

  const defaultValues = {};

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (_values: typeof defaultValues) => {
    if (playerId) {
      cancelPendingDepositLimit({
        playerId,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
        } else if (close) {
          close();
        }
      });
    }
  };

  if (!form || !defaultValues) {
    return null;
  }
  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={playerCancelDepositLimitState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerCancelDepositLimitForm;
